import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import { BottomNextButton } from "components/common/BottomNextButton";
import {
  ONBOARDING_QA_DETAIL_DEFAULT,
  OnboardingQADetailType,
} from "api/onboardingApi";
import useIsInappWebview from "../../../../hooks/useIsInappWebview";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import CheckIcon from "../../../../components/icons/CheckIcon";
import { UpArrowSvg } from "../../../../static/image/common/SvgAssets";
import numWithCircle1NewImg from "../../../../static/image/payment/NumWithCircle_1_new.svg";
import numWithCircle2NewImg from "../../../../static/image/payment/NumWithCircle_2_new.svg";
import ContentTemplateV2 from "../../../../components/task/content/ContentTemplateV2";
import { attachEulLeul } from "../../../../utils/koreanUtils";
import useGetDeviceInfo from "../../../../hooks/useGetDeviceInfo";
import {
  MktVersionTypes,
  versionToPathPrefix,
} from "../../../../components/mkt/mktVersionUtils";
import { onboardingPromotionDataState } from "../../../../recoil/onboardingPromotionDataAtom";
import { goal2OptionList } from "../goal/Goal_2_MultiSelect_1_Promotion";
import { goal3OptionList } from "../goal/Goal_3_MultiSelect_2_Promotion";
import { goal5OptionList } from "../goal/Goal_5_Promotion";
import { motivation7OptionList } from "../motivation/Motivation_7_MultiSelect_Promotion";
import { userSimpleAtom } from "../../../../recoil/userSimpleAtom";
import useAutoLoginPromotion from "../../../../hooks/useAutoLoginPromotion";
import useFetchUserPromotion from "../../../../hooks/useFetchUserPromotion";
import useMixpanelMKT from "../../../../hooks/useMixpanelMKT";
import { useMutation } from "@tanstack/react-query";
import { webFunnelApi } from "../../../../api/webFunnelApi";
import { PATH } from "../../../../routes/paths";

const numberWithCircleNew = [numWithCircle1NewImg, numWithCircle2NewImg];

export default function Curriculum_1_Promotion({
  version = "v4",
}: {
  version?: MktVersionTypes;
}) {
  const isInappWebview = useIsInappWebview();
  const navigate = useNavigate();

  const user = useRecoilValue(userSimpleAtom);
  const { isChecking } = useAutoLoginPromotion();
  const { isFetching } = useFetchUserPromotion();

  const handleNext = () => {
    if (user?.paymentPageType === "m") {
      navigate(`/${versionToPathPrefix(version)}-onboarding/payment/1`, {
        replace: true,
      });
    } else {
      navigate(`/${versionToPathPrefix(version)}-onboarding/product/1`, {
        replace: true,
      });
    }
  };

  const trackMixpanel = useMixpanelMKT({
    eventName: "커리큘럼 페이지 진입",
    version: version,
  });

  const { mutate: trackEvent } = useMutation(
    ({
      eventName,
      properties,
    }: {
      eventName: string;
      properties?: { [key: string]: any };
    }) => webFunnelApi.trackEvent(eventName, properties)
  );

  const pageKey = "result_5";

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    trackMixpanel();
    trackEvent({
      eventName: "커리큘럼 페이지 진입",
      properties: {
        event_source: "web",
      },
    });
  });

  // 로그인 체크 후 리다이렉트
  useEffect(() => {
    if (!isChecking && !isFetching && !user?.accessToken) {
      navigate(PATH.promotionLogin, { replace: true });
    }
  }, [isChecking, isFetching, user, navigate]);

  const promotionData = useRecoilValue(onboardingPromotionDataState);

  const processSelection = (
    key: string,
    optionList: { id: number; text: string }[]
  ) => {
    const data = promotionData[key];
    if (!data) return null;

    const selectedIds = new Set<number>();
    if (data.multiSelectionIdList) {
      data.multiSelectionIdList.forEach((id) => selectedIds.add(id));
    }
    if (data.singleSelectionId) {
      selectedIds.add(data.singleSelectionId);
    }

    if (selectedIds.size === 0) return null;

    const sortedIds = Array.from(selectedIds).sort((a, b) => a - b);

    const collected = sortedIds
      .map((id) => optionList.find((opt) => opt.id === id)?.text)
      .filter((text): text is string => !!text);

    if (collected.length === 0) return null;

    let filtered = [...collected];
    if (filtered.length >= 2 && filtered.includes("기타")) {
      filtered = filtered.filter((t) => t !== "기타");
    }

    const transformed = filtered.map((text) => {
      if (key === "goal_3") {
        return text === "기타" ? "여러 외부적 요인" : text;
      } else if (key === "goal_5") {
        if (text.includes("잘 모르겠음")) {
          return "부정적";
        } else {
          return text.replace(/\s*\(.*\)/, "").trim();
        }
      }
      return text;
    });

    const joined = transformed.join(", ");
    return joined.trim() === "" ? null : joined;
  };

  const onboardingQADetailState: OnboardingQADetailType = {
    pageGoal2Answer:
      processSelection("goal_2", goal2OptionList) ??
      ONBOARDING_QA_DETAIL_DEFAULT.pageGoal2Answer,
    pageGoal3Answer:
      processSelection("goal_3", goal3OptionList) ??
      ONBOARDING_QA_DETAIL_DEFAULT.pageGoal3Answer,
    pageGoal5Answer:
      processSelection("goal_5", goal5OptionList) ??
      ONBOARDING_QA_DETAIL_DEFAULT.pageGoal5Answer,
    motivation7Answer:
      processSelection("motivation_7", motivation7OptionList) ??
      ONBOARDING_QA_DETAIL_DEFAULT.motivation7Answer,
  };

  const SESSION_OPEN_WEEKS_KEY = "payment.openWeeks.100dayABC";
  const [openWeeks, setOpenWeeks] = useState<Record<number, boolean>>(() => {
    try {
      if (typeof window !== "undefined") {
        const saved = sessionStorage.getItem(SESSION_OPEN_WEEKS_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      }
    } catch {}
    return { 0: true };
  });

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          SESSION_OPEN_WEEKS_KEY,
          JSON.stringify(openWeeks)
        );
      }
    } catch {}
  }, [openWeeks]);

  const toggleWeek = (index: number) => {
    setOpenWeeks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const { homeIndicatorHeight } = useGetDeviceInfo();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - homeIndicatorHeight
  );

  // 영역 1: ~님의 프로그램 섹션
  const programSectionWithOnboarding = (
    <Stack
      spacing={"40px"}
      sx={{
        width: "100%",
        maxWidth: "100%",
        mx: "auto",
        px: "16px",
        pt: "12px",
        pb: "80px",
      }}
    >
      <Stack
        spacing={"16px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
          textAlign: "left",
          px: "4px",
        }}
      >
        <CustomTypography
          variant={"H1"}
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 800,
          }}
        >
          {`${user?.nickname}님의 프로그램`}
        </CustomTypography>
        <Typography
          variant={"Subtitle"}
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
          }}
        >
          {"나와 유사한 40만 개의 데이터 포인트로 생성한 맞춤 프로그램입니다."}
        </Typography>
      </Stack>

      <Stack
        spacing={"12px"}
        sx={{
          width: "100%",
          maxWidth: "660px",
        }}
      >
        {/*1-2주차*/}
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "16px",
            py: "14px",
            px: "20px",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              onClick={() => {
                toggleWeek(0);
              }}
              sx={{ height: "36px", cursor: "pointer" }}
            >
              <Box
                sx={{
                  ...(!openWeeks[0] && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Strong} />
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {`1-2주차`}
              </CustomTypography>
            </Stack>

            {openWeeks[0] && (
              <>
                <Stack spacing={2} sx={{ pb: 1 }}>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[0]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`${onboardingQADetailState?.pageGoal3Answer}에 내가 어떻게 괴로워지는지 패턴을 발견합니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[1]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`내게 ${onboardingQADetailState?.pageGoal5Answer} 반응을 일으키는 자동적 사고를 찾아봅니다.`}
                    </CustomTypography>
                  </Stack>

                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      px: 0.2,
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    이 시점에 가능해지는 것
                  </CustomTypography>

                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`내가 왜 힘든지 막연함 없이 말로 설명할 수 있게 됩니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`감정에 휩쓸리기 전에 한 박자 멈추는 순간이 생기기 시작합니다.`}
                    </CustomTypography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Box>

        {/*3-4주차*/}
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "16px",
            py: "14px",
            px: "20px",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              onClick={() => {
                toggleWeek(1);
              }}
              sx={{ height: "36px", cursor: "pointer" }}
            >
              <Box
                sx={{
                  ...(!openWeeks[1] && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Strong} />
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {`3-4주차`}
              </CustomTypography>
            </Stack>

            {openWeeks[1] && (
              <>
                <Stack spacing={2} sx={{ pb: 1 }}>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[0]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`${onboardingQADetailState?.pageGoal5Answer} 반응을 야기하는 자동적 사고를 한 걸음 떨어져 바라보는 연습을 합니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[1]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`생각의 내용을 여러 각도에서 따져보며, 그에 반드시 반응할 필요가 없다는 경험을 쌓아갑니다.`}
                    </CustomTypography>
                  </Stack>

                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      px: 0.2,
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    이 시점에 가능해지는 것
                  </CustomTypography>

                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`생각이 떠올라도 예전만큼 즉각 반응하지 않게 됩니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`나의 마음을 바라보는 메타인지가 강화되기 시작합니다.`}
                    </CustomTypography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Box>

        {/*5-6주차*/}
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "16px",
            py: "14px",
            px: "20px",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              onClick={() => {
                toggleWeek(2);
              }}
              sx={{ height: "36px", cursor: "pointer" }}
            >
              <Box
                sx={{
                  ...(!openWeeks[2] && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Strong} />
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {`5-6주차`}
              </CustomTypography>
            </Stack>

            {openWeeks[2] && (
              <>
                <Stack spacing={2} sx={{ pb: 1 }}>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[0]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`여러 ${onboardingQADetailState?.pageGoal5Answer} 반응에 대해 알아차리고, 분리하고, 따져보는 연습을 계속해 나갑니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[1]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`그 과정에서 회피하거나 싸우지 않고, 있는 그대로를 수용하는 법을 배웁니다.`}
                    </CustomTypography>
                  </Stack>

                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      px: 0.2,
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    이 시점에 가능해지는 것
                  </CustomTypography>

                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`힘든 감정이 와도 예전처럼 버겁게 느껴지지 않습니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`감정과 생각을 없애려 애쓰지 않고 그와 공존할 수 있는 여유를 갖게 됩니다.`}
                    </CustomTypography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Box>

        {/*7-8주차*/}
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "16px",
            py: "14px",
            px: "20px",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              onClick={() => {
                toggleWeek(3);
              }}
              sx={{ height: "36px", cursor: "pointer" }}
            >
              <Box
                sx={{
                  ...(!openWeeks[3] && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Strong} />
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {`7-8주차`}
              </CustomTypography>
            </Stack>

            {openWeeks[3] && (
              <>
                <Stack spacing={2} sx={{ pb: 1 }}>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[0]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`${onboardingQADetailState?.motivation7Answer !== "기타" ? `${onboardingQADetailState?.motivation7Answer}처럼 내 삶에서 정말 중요한 것들을 돌아봅니다.` : "내 삶에서 정말 중요한 것들을 돌아봅니다."}`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[1]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`${onboardingQADetailState?.pageGoal5Answer} 반응 속에서도 ${attachEulLeul(onboardingQADetailState?.pageGoal2Answer ?? "")} 향해 방향을 잡는 법을 배웁니다.`}
                    </CustomTypography>
                  </Stack>

                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      px: 0.2,
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    이 시점에 가능해지는 것
                  </CustomTypography>

                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`기분에 휘둘리지 않고 주체적인 선택을 내리는 순간이 늘어납니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`어떻게 살아가고 싶은지를 기준으로 행동하기 시작합니다.`}
                    </CustomTypography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Box>

        {/*9주차 이상*/}
        <Box
          sx={{
            backgroundColor: theme.vars.palette.Surface.Forward,
            borderRadius: "16px",
            py: "14px",
            px: "20px",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              onClick={() => {
                toggleWeek(4);
              }}
              sx={{ height: "36px", cursor: "pointer" }}
            >
              <Box
                sx={{
                  ...(!openWeeks[4] && {
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                  }),
                  transition: "transform 0.2s",
                }}
              >
                <UpArrowSvg color={theme.vars.palette.Content.Strong} />
              </Box>
              <CustomTypography
                variant={"Body"}
                sx={{
                  p: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {`9주차 이상`}
              </CustomTypography>
            </Stack>

            {openWeeks[4] && (
              <>
                <Stack spacing={2} sx={{ pb: 1 }}>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[0]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`배운 디스턴싱 기술들을 부드럽게 연결하여 삶 속에 적용하는 연습을 계속해나갑니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <img
                      src={numberWithCircleNew[1]}
                      alt={"number"}
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`마음속 깊은 곳을 살펴보며, 어린 시절의 경험들이 지금의 나에게 어떤 영향을 주고 있는지 이해합니다.`}
                    </CustomTypography>
                  </Stack>

                  <CustomTypography
                    variant={"Body"}
                    sx={{
                      px: 0.2,
                      color: theme.vars.palette.Content.Highlight,
                      fontWeight: 700,
                    }}
                  >
                    이 시점에 가능해지는 것
                  </CustomTypography>

                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`내 마음을 하나의 흐름으로 이해할 수 있게 됩니다.`}
                    </CustomTypography>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ pr: "10px" }}
                  >
                    <CheckIcon
                      ariaLabel="included"
                      style={{ marginTop: "0.5px" }}
                    />
                    <CustomTypography
                      variant={"Body"}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineBreak: "auto",
                      }}
                    >
                      {`스스로를 소중히 여기며 돌보는 태도가 자리 잡기 시작합니다.`}
                    </CustomTypography>
                  </Stack>
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Stack>
      <Stack
        sx={{
          width: "100%",
          maxWidth: "660px",
          textAlign: "center",
        }}
      >
        <CustomTypography
          variant={"Caption"}
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
          }}
        >
          {
            "* 선택한 플랜과 참여도에 따라 진행 속도는 달라질 수 있으며, 세부 내용은 코치와 함께 세밀하게 조정됩니다."
          }
        </CustomTypography>
      </Stack>
    </Stack>
  );

  if (isChecking || isFetching || !onboardingQADetailState) {
    return (
      <ContentTemplateV2
        noPadding
        isWide
        setContainerHeight={setContainerHeight}
      >
        <Stack
          sx={{
            width: "100%",
            height: "100vh",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: theme.vars.palette.Background.Level3,
          }}
        >
          <CircularProgress color="neutral" />
        </Stack>
      </ContentTemplateV2>
    );
  }

  return (
    <Stack sx={{ paddingBottom: "60px" }}>
      {programSectionWithOnboarding}
      <BottomNextButton
        disabled={false}
        onClick={handleNext}
        text={"다음"}
        highlightBackground={true}
      />
    </Stack>
  );
}
